// @flow

import React, {useState, useRef, useCallback, forwardRef, useImperativeHandle, useEffect} from 'react';
import styled, { type StyledComponent } from 'styled-components';
import { filterOptions } from '../../../utils/CommonUtils';
import { sizes, fontFamily, colors } from '../../../styles/GlobalStyle';
import { StyledAutoComplete } from '../../../styles/PlacesStyle';
import { Icon } from './Icon';

import TextField from '@material-ui/core/TextField';
// https://github.com/moroshko/autosuggest-highlight
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import { getInputStateOptions } from '../../../utils/I18nUtils';

type InputStateProps = {|
  className?: string,
  value: string,
  name: string,
  label: string,
  id: string,
  invalid?: boolean,
  onChange: Function,
  setStateCode: Function,
  defaultStateValue?: string,
|};

const InputStateRaw = forwardRef<InputStateProps, HTMLInputElement>(
  (
    { className, name, label, id, invalid = false, onChange, setStateCode, defaultStateValue },
    ref
  ) => {
    const innerRef = useRef(null);
    const stateOptions = getInputStateOptions();
    const [inputValue, setInputValue] = useState('');
    const [value, setValue] = useState(null);

    useImperativeHandle(ref, () => innerRef.current);

    useEffect(() => {
      if (defaultStateValue && stateOptions) {
        onChange(defaultStateValue);
        const value = stateOptions.find((s) => s.label === defaultStateValue)
        setValue(value);
        setStateCode(value);
      }
    }, [defaultStateValue]);

    const clearInput = useCallback(() => {
      setInputValue('');
      onChange && onChange('');
    }, [setInputValue]);

    const preventEnter = useCallback((event) => {
      if ((event.charCode || event.keyCode) === 13) {
        event.preventDefault();
        event.stopPropagation();
      }
    }, []);

    const handleFocus = useCallback((event) => {
      // https://stackoverflow.com/a/61973547
      event.target.setAttribute('autocomplete', 'new-password');
    });

    return (
      <span className={`${className || ''}${invalid ? ' invalid' : ''}`}>
        <StyledAutoComplete
          label={label}
          disablePortal={true}
          onKeyDown={(event) => {
            preventEnter(event);
          }}
          onFocus={handleFocus}
          onChange={(event, newValue) => {
            const newValueLabel = newValue ? newValue.label : '';
            // Manually trigger autocomplete onChanged based on parent onChange prop
            setInputValue(newValueLabel);
          }}
          value={value}
          inputValue={inputValue}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue);
            // Manually trigger autocomplete onChanged based on parent onChange prop
            if (onChange) {
              onChange(newInputValue);
            }
          }}
          id={`${id}`}
          options={stateOptions}
          filterOptions={filterOptions}
          getOptionLabel={(option) => {
            setStateCode(option.value || '');
            return option.label || '';
          }} // Need to pass empty string instead of undefined to keep component as controlled
          renderOption={(option, { inputValue }) => {
            const matches = match(option.label, inputValue);
            const parts = parse(option.label, matches);

            return (
              <div>
                {parts.map((part, index) => (
                  <span key={index} style={{ color: part.highlight ? colors.white : '' }}>
                    {part.text}
                  </span>
                ))}
              </div>
            );
          }}
          renderInput={(params) => {
            return (
              <>
                <TextField {...params} label={`${label}`} name={`${name}`} />
                {inputValue && inputValue.length !== 0 && (
                  <div
                    className="input-clear"
                    onClick={clearInput}
                    role="button"
                    tabIndex={-1}
                    onKeyUp={() => {}}
                  >
                    <Icon name="close" />
                  </div>
                )}
              </>
            );
          }}
          getOptionSelected={(option, value) => option.value === value.value}
        />
      </span>
    );
  }
);

InputStateRaw.displayName = 'InputState';

export const InputState: StyledComponent<InputStateProps, {}, typeof InputStateRaw> = styled(
  InputStateRaw
)`
  position: relative;
  display: inline-block;
  width: 100%;
  border-radius: ${sizes.borderRadius};
  font-weight: ${fontFamily.primary.black.fontWeight};

  .suggestions {
    position: absolute;
    width: 100%;
    top: 100px;
    background-color: ${colors.black};
    z-index: 5;
  }

  .input-clear {
    position: absolute;
    right: 0;
    bottom: 20px;
    color: ${colors.yellow};
    cursor: pointer;
    outline: 0;
    transition: 300ms all ease-out;
    animation: scaleIn 0.3s ease-out;
    svg {
      width: 18px;
      height: 18px;
    }
    &:hover {
      color: #fff;
    }
  }

  .suggestion {
    margin-bottom: 5px;
  }
`;
